import React, { useEffect, useRef, useState } from 'react';
import { X, Handshake, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';

import { GradientFrame } from '../ui/GradientFrame';
import Modal from '../ui/Modal';
import { SelectInput, SelectOption } from '../form/SelectInput';
import { TextInput } from '../form/TextInput';
import { TextArea } from '../form/TextArea';
import { SlideInToast } from '../ui/SlideInToast';
import PrivacyCheckbox from '../form/PrivacyCheckbox';

const GAS_ENDPOINT = import.meta.env.VITE_GAS_ENDPOINT as string;

type PartnershipFormModalProps = {
    open: boolean;
    onClose: () => void;
    endpoint?: string;
};

const ROLE_OPTIONS = [
    { value: 'Partner', label: 'Partner' },
    { value: 'Speaker', label: 'Speaker' },
];

export default function PartnershipFormModal({
    open,
    onClose,
    endpoint = GAS_ENDPOINT,
}: PartnershipFormModalProps) {
    const [agree, setAgree] = useState(false);

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [city, setCity] = useState('');
    const [role, setRole] = useState(''); // Partner | Speaker
    const [about, setAbout] = useState(''); // short paragraph
    const [whyUs, setWhyUs] = useState(''); // value for community
    const [link, setLink] = useState(''); // optional

    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] =
        useState<null | { ok: boolean; msg: string }>(null);
    const [toastOpen, setToastOpen] = useState(false);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const firstFieldRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => firstFieldRef.current?.focus(), 30);
        } else {
            setSubmitting(false);
            setSubmitStatus(null);
            setFullName('');
            setEmail('');
            setPhone('');
            setCity('');
            setRole('');
            setAbout('');
            setWhyUs('');
            setLink('');
            setToastOpen(false);
        }
    }, [open]);

    useEffect(() => {
        const onLoad = () => {
            if (!submitting) return;
            setSubmitting(false);
            setSubmitStatus({
                ok: true,
                msg: 'Thanks! We’ll review your request and get back to you soon.',
            });
            setToastOpen(true);
        };
        const iframe = iframeRef.current;
        if (iframe) iframe.addEventListener('load', onLoad);
        return () => {
            if (iframe) iframe.removeEventListener('load', onLoad);
        };
    }, [submitting]);

    function handleSubmit(e: React.FormEvent) {
        if (!endpoint) {
            e.preventDefault();
            setSubmitStatus({ ok: false, msg: 'No GAS endpoint (VITE_GAS_ENDPOINT).' });
            return;
        }
        if (!agree) {
            e.preventDefault();
            setSubmitStatus({ ok: false, msg: 'Please confirm you agree to the data notice.' });
            return;
        }
        setSubmitting(true);
        setSubmitStatus(null);
    }

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <div
                    className={clsx(
                        'relative w-full max-w-[92vw] sm:max-w-2xl my-8 mx-auto transition-all duration-500',
                        'scale-100 opacity-100'
                    )}
                >
                    <GradientFrame>
                        {/* Header */}
                        <div className="relative px-6 sm:px-8 py-6 border-b border-white/10">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f97316]/20 via-[#ec4899]/20 to-[#6366f1]/20 border border-white/10 flex items-center justify-center">
                                    <Handshake className="w-5 h-5 text-[#f97316]" />
                                </div>
                                <h3 className="font-montserrat font-bold text-2xl sm:text-3xl bg-[linear-gradient(90deg,#ff7a1a_0%,#ff8f2f_45%,#ffb867_100%)] bg-clip-text text-transparent">
                                    Let’s Build Something Meaningful Together
                                </h3>
                            </div>
                            <p className="font-montserrat text-slate-300 text-sm">
                                Join ZeroClub as a partner or speaker and contribute to a growing
                                network of ambitious entrepreneurs.
                            </p>

                            <button
                                aria-label="Close"
                                onClick={onClose}
                                className="absolute right-4 top-4 p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300"
                            >
                                <X className="w-5 h-5 text-slate-300" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="relative px-6 sm:px-8 py-6 overflow-y-auto custom-scrollbar max-h-[calc(100dvh-220px)] sm:max-h-[70vh]">
                            <iframe name="partner_iframe" ref={iframeRef} style={{ display: 'none' }} />

                            <form
                                ref={formRef}
                                action={endpoint}
                                method="POST"
                                target="partner_iframe"
                                onSubmit={handleSubmit}
                                className="space-y-5"
                            >
                                <input type="hidden" name="sheet" value="Partnerships" />

                                <TextInput
                                    label="Full Name"
                                    name="fullName"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Jane Smith"
                                    required
                                    ref={firstFieldRef}
                                />

                                <TextInput
                                    label="Email Address"
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="jane@example.com"
                                    required
                                />

                                <TextInput
                                    label="Phone Number"
                                    type="tel"
                                    name="phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+1 (555) 987-6543"
                                    required
                                />

                                <TextInput
                                    label="City"
                                    name="city"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Berlin"
                                    required
                                />

                                <SelectInput
                                    label="I’m interested in becoming"
                                    name="role"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    options={ROLE_OPTIONS}
                                    placeholderLabel="Select a role"
                                    required
                                />

                                <TextArea
                                    label="Tell us more about yourself"
                                    name="about"
                                    value={about}
                                    onChange={(e) => setAbout(e.target.value)}
                                    rows={3}
                                    placeholder="Short paragraph — your background, company, or what you do"
                                    required
                                />

                                <TextArea
                                    label="Why should we partner with you?"
                                    name="whyUs"
                                    value={whyUs}
                                    onChange={(e) => setWhyUs(e.target.value)}
                                    rows={3}
                                    placeholder="Describe how you could bring value to our community — e.g. expertise, products, collaboration idea"
                                    required
                                />

                                <TextInput
                                    label="Website / LinkedIn (optional)"
                                    name="link"
                                    value={link}
                                    onChange={(e) => setLink(e.target.value)}
                                    placeholder="https://www.linkedin.com/in/username or https://yourcompany.com"
                                />


                                <PrivacyCheckbox
                                    checked={agree}
                                    onChange={setAgree}
                                    label={
                                        <>
                                            I agree to send my data to ZeroClub for event participation purposes.
                                            We will never share your contact details.
                                        </>
                                    }
                                />

                                {/* Submit */}
                                <div className="pt-3">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={clsx(
                                            'w-full py-4 rounded-xl font-montserrat font-bold text-white text-lg',
                                            'transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]',
                                            'flex items-center justify-center gap-2',
                                            submitting && 'opacity-70 cursor-not-allowed'
                                        )}
                                        style={{
                                            background: '#d65c30', 
                                            boxShadow: '0 12px 35px -8px rgba(214,92,48,0.55)',
                                        }}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-5 h-5"/>
                                                Submit Partnership Request
                                            </>
                                        )}
                                    </button>

                                </div>

                                {submitStatus && (
                                    <div
                                        className={clsx(
                                            'rounded-xl px-4 py-3 text-sm font-montserrat',
                                            submitStatus.ok
                                                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                                                : 'bg-red-500/10 border border-red-500/30 text-red-400'
                                        )}
                                    >
                                        {submitStatus.msg}
                                    </div>
                                )}
                            </form>
                        </div>
                    </GradientFrame>
                </div>

                <style>{`
          @media (min-width: 640px) {
            .custom-scrollbar::-webkit-scrollbar { width: 10px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.06); border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(180deg, rgba(255,138,74,0.75), rgba(255, 128, 0, 0.65)); border-radius: 10px; border: 1px solid rgba(255,255,255,0.15); }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, rgba(255,138,74,0.95), rgba(255, 108, 50, 0.85)); }
          }
        `}</style>
            </Modal>

            {/* Slide-in toast */}
            <SlideInToast
                open={toastOpen}
                onClose={() => setToastOpen(false)}
                variant={submitStatus?.ok ? 'success' : 'error'}
                title={submitStatus?.ok ? 'Submitted' : submitStatus ? 'Error' : undefined}
                message={submitStatus?.msg}
            />
        </>
    );
}
